import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { PageHeader, Loader } from "@/components/ui-kit";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowLeft, FileDown } from "lucide-react";
import { toast } from "sonner";
import TechSummary from "@/components/technique/TechSummary";
import TechInfo from "@/components/technique/TechInfo";
import TechPlanning from "@/components/technique/TechPlanning";
import TechGantt from "@/components/technique/TechGantt";
import TechAssignments from "@/components/technique/TechAssignments";
import TechMaterials from "@/components/technique/TechMaterials";
import SuiviSection from "@/components/technique/SuiviSection";
import TechReception from "@/components/technique/TechReception";
import TechDocuments from "@/components/technique/TechDocuments";
import TechPhotos from "@/components/technique/TechPhotos";
import { TechHistory, TechTeams } from "@/components/technique/TechHistory";

const safe = (p) => p.then((r) => r.data).catch(() => []);

export default function TechFile() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [suivi, setSuivi] = useState(null);
  const [tick, setTick] = useState(0);
  const [refs, setRefs] = useState({ staff: [], artisans: [], workers: [], articles: [], warehouses: [] });
  const load = useCallback(() => Promise.all([
    api.get(`/technique/files/${id}`).then((r) => setData(r.data)).catch((e) => setData({ error: e.response?.data?.detail || "Dossier introuvable" })),
    api.get(`/technique/files/${id}/suivi`).then((r) => setSuivi(r.data)).catch(() => {}),
  ]).then(() => setTick((t) => t + 1)), [id]);
  useEffect(() => {
    load();
    Promise.all([safe(api.get("/technique/staff")), safe(api.get("/artisans")), safe(api.get("/workers")), safe(api.get("/catalog/articles")), safe(api.get("/warehouses"))])
      .then(([staff, artisans, workers, articles, warehouses]) => setRefs({ staff, artisans, workers, articles, warehouses }));
  }, [load]);
  if (!data) return <Loader />;
  if (data.error) return <p className="py-12 text-center text-slate-500" data-testid="tech-file-denied">{data.error}</p>;
  const f = data.file;
  const closed = f.statut === "Clôturé";
  const opts = { tasks: data.tasks.map((t) => ({ value: t.id, label: t.titre })), assignments: data.assignments.map((a) => ({ value: a.id, label: `${a.ref_name} (${a.kind})` })) };
  const report = async () => {
    try { const r = await api.get(`/technique/files/${f.id}/report-pdf`, { responseType: "blob" }); window.open(URL.createObjectURL(r.data), "_blank"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const S = (kind) => suivi && <SuiviSection kind={kind} fid={f.id} items={suivi[kind] || []} opts={opts} readOnly={closed} reload={load} />;
  const asProps = { data, artisans: refs.artisans, workers: refs.workers, reload: load };
  const tabs = [
    ["infos", "Dossier & contrat", <TechInfo key={f.id + f.statut} data={data} staff={refs.staff} onSaved={load} />],
    ["gantt", "Planning", <TechGantt phases={data.phases} tasks={data.tasks} />],
    ["planning", `Tâches & travaux (${data.tasks.length})`, <TechPlanning data={data} reload={load} />],
    ["documents", "Plans & documents", <TechDocuments fid={f.id} readOnly={closed} />],
    ["artisans", "Artisans", <TechAssignments {...asProps} only="artisan" />],
    ["ouvriers", "Ouvriers", <TechAssignments {...asProps} only="ouvrier" />],
    ["equipes", "Équipes", <TechTeams assignments={data.assignments} />],
    ["materiaux", `Matériaux (${data.material_requests.length})`, <TechMaterials data={data} articles={refs.articles} warehouses={refs.warehouses} reload={load} />],
    ["journal", "Journal de chantier", S("journal")],
    ["rapports", "Rapports", <div className="space-y-3"><Button variant="outline" onClick={report} data-testid="tech-report-pdf-2"><FileDown className="h-4 w-4 mr-1.5" />Rapport global du chantier (PDF)</Button>{S("reports")}</div>],
    ["photos", "Photos", <TechPhotos fid={f.id} refreshKey={tick} />],
    ["qualite", "Contrôle qualité", S("quality")],
    ["incidents", "Incidents", S("incidents")],
    ["reunions", "Réunions", S("meetings")],
    ["reserves", "Réserves", S("reserves")],
    ["reception", "Réception", suivi && <TechReception file={f} suivi={suivi} reload={load} />],
    ["historique", "Historique", <TechHistory fid={f.id} refreshKey={tick} />],
  ];
  return (
    <div data-testid="tech-file-page">
      <Link to="/technique/suivi" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-brand-700 mb-2" data-testid="tech-back"><ArrowLeft className="h-4 w-4" />Suivi technique</Link>
      <PageHeader title={`Suivi technique du projet — ${f.project_name}`} subtitle={`${f.numero} · ${f.client_name || "Client"}${f.contract_numero ? ` · Contrat ${f.contract_numero}` : ""}`}>
        <Button variant="outline" onClick={report} data-testid="tech-report-pdf"><FileDown className="h-4 w-4 mr-1.5" />Rapport PDF</Button>
      </PageHeader>
      <div className="mb-5"><TechSummary file={f} summary={data.summary} /></div>
      <Tabs defaultValue="infos">
        <TabsList className="flex-wrap h-auto justify-start">
          {tabs.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`tech-tab-${v}`}>{l}</TabsTrigger>)}
        </TabsList>
        {tabs.map(([v, , c]) => <TabsContent key={v} value={v}>{c}</TabsContent>)}
      </Tabs>
    </div>
  );
}
